import { useConfirmLeave } from './UnsavedChanges';
import { useQueryClient } from '@tanstack/react-query';
import { logout, orgID, selectOrg, useSession } from '@yingbaba/api';
import { preferredCapability, useWorkspaceUI, type Capability } from '@yingbaba/auth';
import {
  ArrowUpRight,
  Bell,
  BriefcaseBusiness,
  Camera,
  CreditCard,
  FileText,
  Heart,
  Image as ImageIcon,
  LayoutDashboard,
  List,
  LogOut,
  Megaphone,
  Menu,
  MessageSquare,
  Settings,
  ShieldCheck,
  Sparkles,
  Users,
} from '@yingbaba/icons';
import {
  AuthPage,
  Avatar,
  Brand,
  ButtonLink,
  Empty,
  ErrorNotice,
  IconButton,
  LoginRequired,
  Notice,
  Select,
} from '@yingbaba/ui';
import { collaborationOnly, siteURL } from '@yingbaba/utils';
import { useEffect, useState } from 'react';
import { Link, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { AccountSettings } from './Account';
import { DemandList, Favorites, Notifications, Team } from './Business';
import { CreativeStudio, Supply } from './Creative';
import { DigitalResources } from './DigitalResources';
import { ToolsDashboard, ToolsDemandWorkspace, ToolsResources } from './MarketTools';
import { MyTasks } from './MyTasks';
import { MemberCenter } from './MemberCenter';
import { Messages } from './Messages';
import { ProjectDetail, Projects } from './Projects';
import { PromotionStudio } from './PromotionStudio';
import { OrganizationVerification } from './Verification';
const baseNav = [
  ['/', '工作台总览', LayoutDashboard],
  ['/demands', '需求与商机', FileText],
  ['/projects', '我的项目', BriefcaseBusiness],
  ['/tasks', '我的待办', List],
  ['/resources', '影视资源', Camera],
  ['/digital-resources', '数字资源', ImageIcon],
  ['/ai', 'AI 创作', Sparkles],
  ['/promotion', '视频推广', Megaphone],
  ['/finance', '订单与财务', CreditCard],
  ['/membership', '付费会员', CreditCard],
  ['/provider', '作品与服务', ImageIcon],
  ['/team', '组织与团队', Users],
  ['/verification', '主体认证', ShieldCheck],
  ['/messages', '沟通中心', MessageSquare],
  ['/notifications', '系统通知', Bell],
  ['/favorites', '我的收藏', Heart],
  ['/account', '账号设置', Settings],
] as const;
const viewPaths: Record<Capability, string[]> = {
  client: [
    '/',
    '/demands',
    '/projects',
    '/tasks',
    '/resources',
    '/ai',
    '/promotion',
    '/finance',
    '/membership',
    '/digital-resources',
    '/team',
    '/verification',
    '/messages',
    '/notifications',
    '/favorites',
    '/account',
  ],
  provider: [
    '/',
    '/demands',
    '/projects',
    '/tasks',
    '/provider',
    '/resources',
    '/finance',
    '/membership',
    '/digital-resources',
    '/ai',
    '/promotion',
    '/team',
    '/verification',
    '/messages',
    '/notifications',
    '/favorites',
    '/account',
  ],
  resource: [
    '/',
    '/resources',
    '/projects',
    '/tasks',
    '/finance',
    '/membership',
    '/digital-resources',
    '/team',
    '/verification',
    '/messages',
    '/notifications',
    '/favorites',
    '/account',
  ],
};
const viewLabels: Record<Capability, Record<string, string>> = {
  client: { '/demands': '我的需求', '/resources': '我的影视资源', '/finance': '订单与付款' },
  provider: { '/demands': '合作商机', '/provider': '作品与服务', '/finance': '订单与应收' },
  resource: { '/resources': '我的影视资源', '/finance': '订单与收款' },
};
export function App() {
  const loc = useLocation();
  if (loc.pathname === '/login')
    return (
      <>
        <div className="site-header">
          <div className="container">
            <a href={siteURL('www')}>
              <Brand />
            </a>
          </div>
        </div>
        <AuthPage />
      </>
    );
  return (
    <LoginRequired>
      <Shell />
    </LoginRequired>
  );
}
function Shell() {
  const leave = useConfirmLeave();
  const loc = useLocation();
  const session = useSession();
  const qc = useQueryClient();
  const { capability, setCapability, mobileOpen, setMobileOpen } = useWorkspaceUI();
  const [error, setError] = useState<unknown>(null);
  const org = session.data?.organizations.find((o) => o.id === orgID());
  const mailOrganizationID = new URLSearchParams(loc.search).get('organization_id');
  useEffect(() => {
    if (
      !mailOrganizationID ||
      !session.data?.organizations.some((item) => item.id === mailOrganizationID)
    )
      return;
    selectOrg(mailOrganizationID);
    const params = new URLSearchParams(loc.search);
    params.delete('organization_id');
    window.location.replace(`${loc.pathname}${params.size ? `?${params}` : ''}${loc.hash}`);
  }, [mailOrganizationID, session.data, loc.pathname, loc.search, loc.hash]);
  const finance = ['owner', 'admin', 'finance'].includes(org?.role || '');
  useEffect(() => {
    if (org) setCapability(preferredCapability(org.id, org.capabilities.split(',')), org.id);
  }, [org?.id, org?.capabilities]);
  const activeCapability = org?.capabilities.includes(capability) ? capability : 'client';
  const nav = baseNav
    .filter(
      ([path]) =>
        viewPaths[activeCapability].includes(path) &&
        (path !== '/finance' || (finance && !collaborationOnly)),
    )
    .sort(
      (a, b) =>
        viewPaths[activeCapability].indexOf(a[0]) - viewPaths[activeCapability].indexOf(b[0]),
    );
  async function signOut() {
    try {
      await logout();
      qc.clear();
      location.assign('/login');
    } catch (e) {
      setError(e);
    }
  }
  if (mailOrganizationID && session.data)
    return (
      <Empty
        title={
          session.data.organizations.some((item) => item.id === mailOrganizationID)
            ? '正在打开对应组织的业务页面…'
            : '当前账号已无权访问这个组织'
        }
        description="邮件链接仍需验证组织成员资格和业务访问权限。"
        action={
          session.data.organizations.some((item) => item.id === mailOrganizationID) ? undefined : (
            <ButtonLink to="/">返回工作台</ButtonLink>
          )
        }
      />
    );
  return (
    <div className="workspace">
      <a href="#main" className="skip-link">
        跳到主要内容
      </a>
      {mobileOpen && (
        <button
          className="sidebar-overlay"
          aria-label="关闭侧栏"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <aside className={`sidebar ${mobileOpen ? 'open' : ''}`}>
        <a href={siteURL('www')}>
          <Brand />
        </a>
        <Select
          aria-label="当前业务身份"
          className="capability-selector"
          value={activeCapability}
          onChange={(e) => setCapability(e as Capability, org?.id)}
        >
          <option value="client">采购方工作台</option>
          {org?.capabilities.includes('provider') && <option value="provider">服务商工作台</option>}
          {org?.capabilities.includes('resource') && <option value="resource">资源方工作台</option>}
        </Select>
        <span className="sidebar-label">创作与协作</span>
        <nav className="sidebar-nav" aria-label="工作台导航">
          {nav.map(([path, label, I]) => (
            <NavLink key={path} to={path} end={path === '/'} onClick={() => setMobileOpen(false)}>
              <I size={18} strokeWidth={1.6} />
              {viewLabels[activeCapability][path] || label}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-foot">
          <a href={siteURL('www', '/help')}>
            帮助中心
            <ArrowUpRight size={15} />
          </a>
          <a href={siteURL('www')}>
            返回影爸爸官网
            <ArrowUpRight size={15} />
          </a>
          <span style={{ color: '#809587', fontSize: 10, display: 'block', marginTop: 12 }}>
            让每一份创意，都有好的归宿。
          </span>
        </div>
      </aside>
      <div className="workspace-body">
        <header className="workspace-header">
          <div className="row">
            <IconButton
              label="打开工作台导航"
              className="mobile-toggle"
              onClick={() => setMobileOpen(true)}
            >
              <Menu size={20} />
            </IconButton>
            <Select
              aria-label="切换组织"
              value={org?.id || ''}
              onChange={(e) =>
                leave(() => {
                  selectOrg(e);
                  qc.clear();
                  location.assign('/');
                })
              }
            >
              {session.data?.organizations.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="workspace-user">
            <a href={siteURL('www', '/demands')} className="muted user-name">
              发现商机
              <ArrowUpRight size={13} style={{ verticalAlign: 'middle', marginLeft: 5 }} />
            </a>
            <Link to="/messages" className="icon-btn" aria-label="沟通中心">
              <Bell size={17} />
            </Link>
            <Avatar
              name={session.data?.account.name || ''}
              src={session.data?.account.avatar_url}
            />
            <span className="user-name">{session.data?.account.name}</span>
            <IconButton label="退出登录" onClick={() => leave(() => void signOut())}>
              <LogOut size={16} />
            </IconButton>
          </div>
        </header>
        <main id="main" className="workspace-main">
          <ErrorNotice error={error} />
          {session.data?.account &&
            !session.data.account.email_verified_at &&
            loc.pathname !== '/account' && (
              <Notice>
                {new URLSearchParams(loc.search).get('email_delivery') === 'failed' &&
                  '账号已创建，验证邮件暂未发送成功。'}
                登录邮箱尚未验证。项目协作与草稿可继续；公开发布与报名合作前请先验证。{' '}
                <Link to="/account">验证或更换邮箱</Link>
              </Notice>
            )}
          {org && org.verification_status !== 'verified' && loc.pathname !== '/verification' && (
            <Notice>
              {org.verification_materials_required
                ? '当前组织需要完善主体认证资料。'
                : '当前组织尚未完成主体认证。'}
              你可以先准备资料和草稿；公开发布与报名合作需要认证通过。{' '}
              <Link to="/verification">前往主体认证</Link>
            </Notice>
          )}
          {session.data?.account.lifecycle_status === 'closing' && (
            <Notice>
              账号处于注销冷静期，工作空间只读。<Link to="/account">查看或撤销注销申请</Link>
            </Notice>
          )}
          {org?.lifecycle_status && org.lifecycle_status !== 'active' && (
            <Notice>
              当前组织已停用，工作空间只读。<Link to="/team">查看组织管理</Link>
            </Notice>
          )}
          <Routes>
            <Route path="/" element={<ToolsDashboard />} />
            <Route path="/demands" element={<DemandList />} />
            <Route path="/demands/:id" element={<ToolsDemandWorkspace />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/tasks" element={<MyTasks />} />
            <Route path="/projects/:id" element={<ProjectDetail />} />
            <Route path="/resources" element={<ToolsResources />} />
            <Route path="/provider" element={<Supply kind="provider" />} />
            <Route path="/ai" element={<CreativeStudio />} />
            <Route path="/promotion" element={<PromotionStudio />} />
            <Route
              path="/finance"
              element={
                <Empty
                  title="交易由合作双方自主安排"
                  description="影爸爸提供沟通与项目管理工具。"
                  action={<ButtonLink to="/projects">进入项目工作区</ButtonLink>}
                />
              }
            />
            <Route path="/membership" element={<MemberCenter />} />
            <Route path="/digital-resources" element={<DigitalResources />} />
            <Route path="/team" element={<Team />} />
            <Route path="/verification" element={<OrganizationVerification />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/favorites" element={<Favorites />} />
            <Route path="/account" element={<AccountSettings />} />
            <Route
              path="*"
              element={
                <Empty title="页面不存在" action={<ButtonLink to="/">回到工作台</ButtonLink>} />
              }
            />
          </Routes>
        </main>
      </div>
    </div>
  );
}
